<template>
  <div>
    <div :class="$vuetify.breakpoint.mdAndUp ? 'd-flex' : ''">
      <h2 class="mb-2">
        Your Profile
        <v-chip
          dark
          small
          class="ml-2"
          :color="profile.active ? 'green' : 'red'"
        >
          {{ profile.active ? "ACTIVE" : "INACTIVE" }} ACCOUNT
        </v-chip>
      </h2>
      <v-spacer></v-spacer>
      <v-btn
        @click="update = !update"
        :color="update ? 'warning' : 'secondary'"
      >
        {{ update ? "Updating" : "Update" }} Profile
      </v-btn>
    </div>

    <p class="mt-6 red--text">
      <em>Click the Update Profile button to make changes</em>
    </p>

    <v-form
      class="my-7"
      v-model="valid"
      :readonly="!update"
      @submit.prevent="updateProfile"
    >
      <v-card class="pa-5 mb-7">
        <h4 class="mb-5 blue-grey--text">PERSONAL INFO</h4>
        <v-row>
          <v-col cols="12" md="2">
            <v-select
              :items="titles"
              outlined
              label="Title *"
              :rules="rules.required"
              v-model="profile.title"
            ></v-select>
          </v-col>
          <v-col cols="12" md="3">
            <v-text-field
              outlined
              label="First Name *"
              :rules="rules.required"
              v-model="profile.firstName"
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="3">
            <v-text-field
              outlined
              label="Last Name *"
              :rules="rules.required"
              v-model="profile.lastName"
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="4">
            <v-text-field
              outlined
              label="Other Name"
              v-model="profile.otherName"
            ></v-text-field>
          </v-col>
        </v-row>
      </v-card>

      <v-card class="pa-5 mb-7">
        <h4 class="mb-5 blue-grey--text">CONTACT INFO</h4>
        <v-row>
          <v-col cols="12" md="4">
            <v-text-field
              outlined
              disabled
              validate-on-blur
              label="Primary Email *"
              v-model="profile.email"
              :rules="[...rules.required, ...rules.email]"
            ></v-text-field>
          </v-col>

          <v-col cols="12" md="4">
            <v-text-field
              outlined
              validate-on-blur
              label="Alternative Email"
              v-model="profile.email2"
              :rules="rules.email"
            ></v-text-field>
          </v-col>

          <v-col cols="12" md="4">
            <v-text-field
              outlined
              validate-on-blur
              label="Phone"
              v-model="profile.phone"
            ></v-text-field>
          </v-col>
        </v-row>
      </v-card>

      <v-card class="pa-5 mb-7">
        <h4 class="mb-5 blue-grey--text">PROFESSIONAL INFO</h4>
        <v-row>
          <v-col cols="12" md="6">
            <v-text-field
              outlined
              label="Specialization"
              validate-on-blur
              v-model="profile.specialization"
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field
              outlined
              label="ORCID iD"
              validate-on-blur
              v-model="profile.orcid"
            ></v-text-field>
          </v-col>
        </v-row>
      </v-card>

      <v-card class="pa-5 mb-7">
        <h4 class="mb-5 blue-grey--text">WORK INFO</h4>
        <v-row>
          <v-col cols="12" md="4">
            <v-combobox
              v-model="profile.institution"
              :items="institutions"
              item-text="name"
              item-value="name"
              label="Institution name *"
              outlined
              :return-object="false"
              :rules="rules.required"
              validate-on-blur
            ></v-combobox>
          </v-col>

          <v-col cols="12" md="4">
            <v-text-field
              outlined
              validate-on-blur
              label="Department / College *"
              :rules="rules.required"
              v-model="profile.department"
            ></v-text-field>
          </v-col>

          <v-col cols="12" md="4">
            <v-autocomplete
              outlined
              :items="countries"
              label="Country"
              validate-on-blur
              v-model="profile.country"
            ></v-autocomplete>
          </v-col>

          <v-col cols="12" md="4">
            <v-autocomplete
              outlined
              :items="states(profile.country)"
              label="State"
              validate-on-blur
              v-model="profile.state"
            ></v-autocomplete>
          </v-col>

          <v-col cols="12" md="8">
            <v-text-field
              outlined
              label="Address"
              validate-on-blur
              v-model="profile.address"
            ></v-text-field>
          </v-col>
        </v-row>
      </v-card>

      <v-card class="pa-5 mb-7">
        <h4 class="mb-5 blue-grey--text">
          WORK INFO 2 <small>(OPTIONAL)</small>
        </h4>
        <v-row>
          <v-col cols="12" md="4">
            <v-combobox
              v-model="profile.institution2"
              :items="institutions"
              item-text="name"
              item-value="name"
              label="Institution name"
              outlined
              :return-object="false"
              validate-on-blur
            ></v-combobox>
          </v-col>

          <v-col cols="12" md="4">
            <v-text-field
              outlined
              validate-on-blur
              label="Department / College"
              v-model="profile.department2"
            ></v-text-field>
          </v-col>

          <v-col cols="12" md="4">
            <v-autocomplete
              outlined
              :items="countries"
              label="Country"
              validate-on-blur
              v-model="profile.country2"
            ></v-autocomplete>
          </v-col>

          <v-col cols="12" md="4">
            <v-autocomplete
              outlined
              :items="states(profile.country2)"
              label="State"
              validate-on-blur
              v-model="profile.state2"
            ></v-autocomplete>
          </v-col>

          <v-col cols="12" md="8">
            <v-text-field
              outlined
              label="Address"
              validate-on-blur
              v-model="profile.address2"
            ></v-text-field>
          </v-col>
        </v-row>
      </v-card>

      <div v-if="update">
        <p class="mb-7">* mark required fields.</p>
        <v-btn type="submit" large color="primary"> Save Changes </v-btn>
      </div>
    </v-form>
  </div>
</template>


<script>
import profileTemplate from "../../dtos/user";
import { required, email, phone } from "../../tools/rules";
import { USER_TITLES } from "../../tools/specs";
import Institutions from "../../data/world_universities_and_domains-min.json";
import { listCountries, listStates } from "../../helpers/address";

export default {
  data: () => ({
    profile: profileTemplate,
    rules: {
      required,
      email,
      phone,
    },
    valid: false,
    update: false,
    updating: false,
    titles: USER_TITLES,
    institutions: Institutions,
  }),

  computed: {
    countries: listCountries,
  },

  methods: {
    states: listStates,

    fetchProfile() {
      this.$axios.get("/profile").then(({ data: profile }) => {
        this.profile = profile;
      });
    },

    updateProfile() {
      if (!this.updating) {
        this.updating = true;

        this.$axios
          .put("/profile", { ...this.profile })
          .then(() => {
            this.fetchProfile();
            this.update = false;
          })
          .finally(() => {
            this.updating = false;
          });
      }
    },
  },

  mounted() {
    this.fetchProfile();
  },
};
</script>